import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { getComments, addComment } from "../request-orders.service";
import { formatDate } from "shared/helpers/utils";
import CloseIcon from "shared/assets/images/svg/close_form.svg";
import "../requestOrders.scss";

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const AttachIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const FileIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const PdfIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#e53935" />
    <polyline points="14 2 14 8 20 8" stroke="#e53935" />
    <text x="7" y="18" fontSize="7" fontWeight="bold" fill="#e53935" fontFamily="Arial">PDF</text>
  </svg>
);

const ChatDrawer = ({ show, onHide, categoryId, requestId, requestTitle }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchComments = useCallback(async (silent) => {
    if (!requestId || !categoryId) return;
    if (!silent) setLoading(true);
    try {
      const data = await getComments(Number(requestId), Number(categoryId));
      setComments(data);
    } catch {
      // error handled by client
    } finally {
      if (!silent) setLoading(false);
    }
  }, [requestId, categoryId]);

  useEffect(() => {
    if (show) {
      fetchComments();
      const interval = setInterval(() => fetchComments(true), 2000);
      return () => clearInterval(interval);
    } else {
      setComments([]);
      setMessage("");
      setFiles([]);
    }
  }, [show, fetchComments]);

  useEffect(() => {
    if (show) {
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [comments, show]);

  const readFileAsBase64 = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve([file.name, reader.result.split(",")[1]]);
      reader.readAsDataURL(file);
    });

  const handleSend = async () => {
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      const fileArrays = await Promise.all(files.map(readFileAsBase64));
      const desc = message.trim() || " ";
      await addComment(Number(categoryId), Number(requestId), desc, fileArrays);
      setMessage("");
      setFiles([]);
      await fetchComments();
    } catch {
      // error handled by client
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={show}
      onClose={onHide}
      onOpen={() => {}}
    >
      <Box role="presentation" className="chatDrawer">
        <div className="drawerHeader">
          <div className="info">
            <h2><span className="header-icon"><ChatIcon /></span> {requestTitle || `#${requestId}`}</h2>
          </div>
          <div className="action">
            <img src={CloseIcon} alt="close" onClick={onHide} />
          </div>
        </div>

        <div className="chatDrawer__messages">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : comments.length === 0 ? (
            <div className="chat-empty">
              <ChatIcon />
              <p>{t("request-orders.noComments")}</p>
            </div>
          ) : (
            [...comments].reverse().map((c, index) => (
              <div
                key={index}
                className={`chat-bubble ${c.isforCustomer ? "customer" : "support"}`}
              >
                <div className="bubble-avatar">
                  {c.isforCustomer ? "Y" : "S"}
                </div>
                <div className="bubble-content">
                  {c.comment && c.comment.trim() && (
                    <p className="bubble-text">{c.comment}</p>
                  )}
                  {c.files && c.files.length > 0 && (
                    <div className="bubble-files">
                      {c.files.map((file, fi) => {
                        const ext = (file.fileExtension || "").toLowerCase();
                        const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
                        const isPdf = ext === "pdf";
                        return (
                          <a
                            key={fi}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={isImage ? "file-image-link" : isPdf ? "file-pdf-link" : "file-link"}
                          >
                            {isImage ? (
                              <img src={file.url} alt={file.fileName} className="bubble-image" />
                            ) : isPdf ? (
                              <span className="pdf-attachment">
                                <PdfIcon />
                                <span className="pdf-name">{file.fileName}</span>
                              </span>
                            ) : (
                              <><FileIcon /> {file.fileName}</>
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                  <span className="bubble-time">{formatDate(c.createdTime)}</span>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="chatDrawer__input">
          {files.length > 0 && (
            <div className="attached-files">
              {files.map((f, i) => (
                <span key={i} className="file-chip">
                  <FileIcon /> {f.name}
                  <button className="remove-file" onClick={() => removeFile(i)}>
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="chat-input-row">
            <button
              className="btn-attach"
              onClick={() => fileInputRef.current?.click()}
              title={t("request-orders.attachFile")}
            >
              <AttachIcon />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
              onChange={handleFileSelect}
            />
            <textarea
              className="chat-input"
              placeholder={t("request-orders.typeMessage")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button
              className="btn-send"
              onClick={handleSend}
              disabled={sending || (!message.trim() && files.length === 0)}
            >
              {sending ? "..." : <SendIcon />}
            </button>
          </div>
        </div>
      </Box>
    </SwipeableDrawer>
  );
};

export default ChatDrawer;
